/**
 * M2.2 · Buddy Avanza — Panel del docente institucional.
 *
 * Consume EXCLUSIVAMENTE:
 *   GET /api/avanzo-b2c/teacher/students-progress
 *
 * Contrato:
 *   - Frontend only. No writes. No student endpoints.
 *   - No expone IDs internos, emails, cost, tokens, model, etc.
 *   - No incluye acciones pedagógicas (M2.2 solo lectura).
 *   - No usa AvanzoShell (aparte de las utilidades de estilos compartidos).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertCircle, LogOut, RefreshCw, Users } from "lucide-react";
import BuddyAvanzaLegalFooter from "@/components/BuddyAvanzaLegalFooter";
import { teacherStudentsProgress } from "@/lib/avanzoApi";
import { API } from "@/App";
import "./avanzo.css";

// ── Utilities ────────────────────────────────────────────────

const STATUS_LABELS = {
  not_started:        "Sin iniciar",
  in_progress:        "En progreso",
  activity_available: "Con actividad",
};

const STATUS_TONE = {
  not_started:        "avz-tp-pill--muted",
  in_progress:        "avz-tp-pill--warn",
  activity_available: "avz-tp-pill--ok",
};

function humanDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const startOfDay = (x) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round(
    (startOfDay(now) - startOfDay(d)) / (24 * 3600 * 1000)
  );
  if (diffDays === 0) return "Hoy";
  if (diffDays === 1) return "Ayer";
  // Spanish short month names
  const months = ["ene","feb","mar","abr","may","jun",
                  "jul","ago","sep","oct","nov","dic"];
  const label = `${d.getDate()} ${months[d.getMonth()]}`;
  // Include year if different from current
  if (d.getFullYear() !== now.getFullYear()) {
    return `${label} ${d.getFullYear()}`;
  }
  return label;
}

function pct(value) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `${Math.round(n)}%`;
}

function masteryPct(value) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `${Math.round(n * 100)}%`;
}

function count(value) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return String(n);
}

// ── Sub-components ───────────────────────────────────────────

function TeacherHeader() {
  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    try { localStorage.removeItem("token"); } catch { /* noop */ }
    navigate("/login", { replace: true });
  };
  return (
    <header className="avz-nav">
      <div className="avz-nav-inner">
        <div className="avz-brand" data-testid="avz-brand">
          <img src="/qhub-buddy-logo.png" alt="QHUB.BUDDY"
               className="avz-brand-logo" />
          <span className="avz-brand-sub">Buddy Avanza · Docente</span>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="avz-nav-logout"
          data-testid="avz-teacher-logout"
          aria-label="Cerrar sesión"
        >
          <LogOut className="avz-nav-logout-icon" />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </header>
  );
}

function SummaryCards({ students }) {
  const total = students.length;
  const withActivity = students.filter(
    (s) => s.activity_status === "activity_available"
  ).length;
  const inProgress = students.filter(
    (s) => s.activity_status === "in_progress"
  ).length;
  const notStarted = students.filter(
    (s) => s.activity_status === "not_started"
  ).length;

  const cards = [
    { key: "total",     label: "Estudiantes asignados",  value: total,
      testid: "avz-tp-summary-total" },
    { key: "activity",  label: "Con actividad",          value: withActivity,
      testid: "avz-tp-summary-activity",  tone: "ok"   },
    { key: "progress",  label: "En progreso",            value: inProgress,
      testid: "avz-tp-summary-progress",  tone: "warn" },
    { key: "notstart",  label: "Sin iniciar",            value: notStarted,
      testid: "avz-tp-summary-notstart",  tone: "muted"},
  ];
  return (
    <div className="avz-tp-summary" data-testid="avz-tp-summary">
      {cards.map((c) => (
        <div
          key={c.key}
          className={`avz-tp-card${c.tone ? " avz-tp-card--" + c.tone : ""}`}
          data-testid={c.testid}
        >
          <span className="avz-tp-card-value">{c.value}</span>
          <span className="avz-tp-card-label">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const label = STATUS_LABELS[status] || "—";
  const tone = STATUS_TONE[status] || "avz-tp-pill--muted";
  return (
    <span className={`avz-tp-pill ${tone}`} data-testid={`avz-tp-status-${status}`}>
      {label}
    </span>
  );
}

function StudentsTable({ students }) {
  return (
    <div className="avz-tp-table-wrap" data-testid="avz-tp-table-wrap">
      <table className="avz-tp-table" data-testid="avz-tp-table">
        <thead>
          <tr>
            <th scope="col">Estudiante</th>
            <th scope="col">Grado / sección</th>
            <th scope="col">Estado</th>
            <th scope="col">Última actividad</th>
            <th scope="col">Entrenamientos</th>
            <th scope="col">Progress Check</th>
            <th scope="col">Simulacro 1</th>
            <th scope="col">Simulacro final</th>
            <th scope="col">Dominio</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.student_profile_id}
                data-testid={`avz-tp-row-${s.student_profile_id}`}>
              <td className="avz-tp-name">{s.student_name || "—"}</td>
              <td>
                {s.grade || "—"}
                {s.section ? ` · ${s.section}` : ""}
              </td>
              <td><StatusPill status={s.activity_status} /></td>
              <td>{humanDate(s.last_activity_at)}</td>
              <td>{count(s.training_sessions_completed)}</td>
              <td>{pct(s.latest_progress_check_percentage)}</td>
              <td>{pct(s.latest_simulation1_percentage)}</td>
              <td>{pct(s.latest_final_simulation_percentage)}</td>
              <td>{masteryPct(s.mastery_average)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StudentsCards({ students }) {
  return (
    <div className="avz-tp-cards" data-testid="avz-tp-cards">
      {students.map((s) => (
        <article
          key={s.student_profile_id}
          className="avz-tp-student-card"
          data-testid={`avz-tp-card-${s.student_profile_id}`}
        >
          <header className="avz-tp-student-header">
            <span className="avz-tp-student-name">{s.student_name || "—"}</span>
            <StatusPill status={s.activity_status} />
          </header>
          <div className="avz-tp-student-grade">
            {s.grade || "—"}
            {s.section ? ` · ${s.section}` : ""}
            <span className="avz-tp-student-sep">·</span>
            Última: {humanDate(s.last_activity_at)}
          </div>
          <dl className="avz-tp-student-metrics">
            <div>
              <dt>Entrenamientos</dt>
              <dd>{count(s.training_sessions_completed)}</dd>
            </div>
            <div>
              <dt>Progress Check</dt>
              <dd>{pct(s.latest_progress_check_percentage)}</dd>
            </div>
            <div>
              <dt>Simulacro 1</dt>
              <dd>{pct(s.latest_simulation1_percentage)}</dd>
            </div>
            <div>
              <dt>Simulacro final</dt>
              <dd>{pct(s.latest_final_simulation_percentage)}</dd>
            </div>
            <div>
              <dt>Dominio</dt>
              <dd>{masteryPct(s.mastery_average)}</dd>
            </div>
          </dl>
        </article>
      ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────

export default function AvanzoTeacherPanel() {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const res = await teacherStudentsProgress();
      const d = res.data || {};
      setState({
        loading: false,
        error: null,
        data: { students: Array.isArray(d.students) ? d.students : [],
                count: Number(d.count || 0) },
      });
    } catch (err) {
      const status = err?.response?.status;
      const kind = status === 403 ? "forbidden" : "error";
      setState({ loading: false, error: { kind, status }, data: null });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="avz-shell" data-testid="avz-teacher-panel">
      <TeacherHeader />
      <main className="avz-container avz-tp-container">
        <section className="avz-tp-hero">
          <h1 className="avz-tp-title">Buddy Avanza</h1>
          <p className="avz-tp-subtitle">Progreso de tus estudiantes</p>
          <p className="avz-tp-lede">
            Consulta la actividad y avance de los estudiantes que tienes
            asignados.
          </p>
        </section>

        {state.loading && (
          <div className="avz-tp-loading" data-testid="avz-tp-loading"
               role="status" aria-live="polite">
            <div className="avz-tp-spinner" aria-hidden="true" />
            <span>Cargando estudiantes…</span>
          </div>
        )}

        {!state.loading && state.error?.kind === "forbidden" && (
          <div className="avz-tp-alert avz-tp-alert--warn"
               data-testid="avz-tp-forbidden" role="alert">
            <AlertCircle aria-hidden="true" />
            <div>
              <strong>Acceso a Buddy Avanza no disponible.</strong>
              <p>Buddy Avanza no está habilitado para tu institución en este
                 momento. Comunícate con tu administración institucional.</p>
            </div>
          </div>
        )}

        {!state.loading && state.error?.kind === "error" && (
          <div className="avz-tp-alert avz-tp-alert--error"
               data-testid="avz-tp-error" role="alert">
            <AlertCircle aria-hidden="true" />
            <div>
              <strong>No pudimos cargar el progreso en este momento.</strong>
              <p>Intenta nuevamente en unos segundos.</p>
            </div>
            <button
              type="button"
              className="avz-tp-retry"
              onClick={load}
              data-testid="avz-tp-retry"
            >
              <RefreshCw aria-hidden="true" /> Reintentar
            </button>
          </div>
        )}

        {!state.loading && !state.error && state.data && (
          <>
            <SummaryCards students={state.data.students} />

            {state.data.count === 0 ? (
              <div className="avz-tp-empty" data-testid="avz-tp-empty">
                <Users aria-hidden="true" />
                <p>Aún no tienes estudiantes disponibles en Buddy Avanza.</p>
              </div>
            ) : (
              <>
                <StudentsTable students={state.data.students} />
                <StudentsCards  students={state.data.students} />
              </>
            )}
          </>
        )}
      </main>
      <BuddyAvanzaLegalFooter dense />
    </div>
  );
}
